/** Types du module Notes de frais salariés. */

/** Périmètre de visibilité — le RÔLE. Pas de niveau « pairs » : silo hiérarchique. */
export type RoleVisibilite = 'soi' | 'equipe' | 'entreprise';
export type TypeVehicule = 'voiture' | 'deux_roues_50_plus' | 'cyclomoteur';
export type Motorisation = 'thermique' | 'hybride' | 'electrique';
export type RegleTva = 'saisie' | 'forcee_zero';
/** Cycle de vie du compte utilisateur porté par la fiche salarié. */
export type StatutCompte = 'cree' | 'invite' | 'actif' | 'suspendu' | 'desactive';

export interface Anciennete {
  mois: number;
  libelle: string;
}

export interface Salarie {
  id: string;
  civilite: string | null;
  prenom: string;
  nom: string;
  matricule: string | null;
  email: string;
  fonction: string | null;

  num_secu?: string | null;
  date_naissance?: string | null;
  lieu_naissance?: string | null;
  adresse_rue?: string | null;
  adresse_cp?: string | null;
  adresse_ville?: string | null;
  portable?: string | null;

  date_debut_contrat?: string | null;
  type_contrat?: string | null;
  statut_rh?: string | null;
  fin_periode_essai_1?: string | null;
  fin_periode_essai_2?: string | null;
  periode_essai_indicative?: boolean;
  date_fin_contrat?: string | null;
  motif_depart?: string | null;

  manager_id?: string | null;
  responsable_rh_id?: string | null;

  /** Kilomètres déjà parcourus dans l'année avant l'arrivée dans Gertrude. */
  cumul_ik_initial_km?: number | null;
  cumul_ik_initial_annee?: number | null;

  /** Congés — 'forfait_jour' | '35_heures'. Seul le forfait jour ouvre des RTT. */
  modalite_temps_travail?: string | null;
  /** Stagiaires : les 2 jours mensuels se cumulent-ils sur la durée du stage ? */
  cumul_absences_autorisees?: boolean;
  /** Décoché pour les mandataires sociaux : le module Congés les ignore (D3.6). */
  beneficie_conges?: boolean;

  // Les droits ne sont plus portés par la fiche depuis la refonte du socle
  // d'identité (05/08/2026) : ils vivent sur le COMPTE (écran Utilisateurs).
  // Les déclarer ici faisait croire que l'API les renvoyait — elle ne l'a
  // jamais fait, et tout code qui les lisait obtenait `undefined`.

  zoho_vendor_id?: string | null;
  beneficiaire_id?: string | null;

  avance_frais?: boolean;
  avance_montant?: number | null;
  avance_date?: string | null;
  avance_recuperee_stc?: boolean;

  statut_compte: StatutCompte;
  invite_le?: string | null;
  derniere_connexion_at?: string | null;
  anciennete?: Anciennete | null;
}

export interface Vehicule {
  id: string;
  salarie_id: string;
  type_vehicule: TypeVehicule;
  cv_fiscaux: number | null;
  motorisation: Motorisation;
  immatriculation: string | null;
  carte_grise_filename: string | null;
  carte_grise_presente?: boolean;
  actif: boolean;
}

export interface RefValeur {
  id: string;
  liste: string;
  valeur: string;
  libelle: string;
  ordre: number;
  actif: boolean;
}

export interface CategorieNdf {
  id: string;
  parent_id: string | null;
  code: string;
  libelle: string;
  regle_tva: RegleTva;
  sous_categorie_obligatoire: boolean;
  accepte_sous_categories: boolean;
  quantite_defaut: number;
  champ_invites: boolean;
  est_ik: boolean;
  est_abonnement: boolean;
  ordre: number;
  actif: boolean;
}

export interface CompteAnalytique {
  id: string;
  code: string;
  libelle: string;
  contexte: string | null;
  article_requis: boolean;
  zoho_item_name: string | null;
  zoho_account_name: string | null;
  actif: boolean;
}

export interface LigneBareme {
  id: string;
  annee: number;
  type_vehicule: TypeVehicule;
  tranche_cv: string | null;
  distance_min: number;
  distance_max: number | null;
  electrique: boolean;
  coefficient: number;
  part_fixe: number;
  source_officielle: string | null;
}

export interface ParametreNdf {
  key: string;
  value: string | null;
}

/** Ce que le module sait du visiteur — sert à l'affichage, jamais à l'autorisation. */
export interface MoiNdf {
  utilisateur: { id: string; nom: string | null; email: string; statut_compte: StatutCompte };
  salarie: {
    id: string;
    prenom: string;
    nom: string;
    matricule: string | null;
    /** Faux = mandataire social : ni congés, ni arrêts, ni tickets. */
    beneficie_conges: boolean;
  } | null;
  profil: string | null;
  role: string | null;
  portee: RoleVisibilite;
  /** Actions autorisées par module. L'écran s'en sert pour n'afficher que l'utile. */
  permissions: Record<string, string[]>;
  apercu?: boolean;
  /** Pré-production : on peut y endosser un autre profil ET agir en son nom. */
  bac_a_sable?: boolean;
}

export interface UtilisateurCourt {
  id: string;
  nom: string | null;
  email: string;
  statut_compte: StatutCompte;
  profil: string | null;
}

/** Raccourci de lecture des permissions du module notes de frais. */
export function peutNdf(moi: MoiNdf | undefined, action: string): boolean {
  return peutModule(moi, 'ndf', action);
}

/** Même lecture, pour n'importe quel module RH — les congés, demain d'autres. */
export function peutModule(moi: MoiNdf | undefined, module: string, action: string): boolean {
  return Boolean(moi?.permissions?.[module]?.includes(action));
}

// ─── Dépenses ───────────────────────────────────────────────────────────────

export interface AlerteDepense {
  code: string;
  niveau: 'bloquant' | 'alerte' | 'tag';
  message: string;
  /** Ce que le VALIDEUR doit savoir : conformité, risque, conséquence. */
  messageValideur?: string;
  pourValideur?: boolean;
  details?: Record<string, unknown>;
}

export interface Depense {
  id: string;
  salarie_id: string;
  date_depense: string;
  categorie_id: string;
  sous_categorie_id: string | null;
  description: string | null;
  quantite: number;
  prix_unitaire_ht: number;
  montant_tva: number;
  montant_ttc: number;
  devise: string | null;
  montant_original: number | null;
  taux_change: number | null;
  demande_remboursement: boolean;
  facturable: boolean;
  client_crm_id: string | null;
  client_nom: string | null;
  mission_crm_id: string | null;
  mission_nom: string | null;
  type_mission: string | null;
  invites: string | null;
  statut: string;
  /** Note de frais dans laquelle la dépense est rangée, s'il y en a une. */
  note_id: string | null;
  /** Motif du pointage par un valideur, quand la dépense est en anomalie. */
  anomalie_motif?: string | null;
  /** A-t-elle déjà été pointée lors d'un circuit précédent ? (R-VAL-02) */
  deja_en_anomalie?: boolean;
  controlee_rh_at?: string | null;
  // Ce que l'analyse du justificatif a lu, conservé à côté de ce que le
  // salarié a validé : sert aux contrôles et à la comparaison de doublons.
  commercant: string | null;
  date_extraite: string | null;
  type_document: string | null;
  /** Une pièce est-elle réellement attachée ? Pilote le bouton d'aperçu. */
  a_justificatif?: boolean;
  alertes: AlerteDepense[];
}

/** Ce que l'IA a lu sur un justificatif — tout est facultatif et corrigeable. */
export interface ChampsExtraits {
  date: string | null;
  categorie_code: string | null;
  sous_categorie_code: string | null;
  description: string | null;
  commercant: string | null;
  montant_ht: number | null;
  montant_tva: number | null;
  montant_ttc: number | null;
  devise: string | null;
  type_document: string | null;
  peage_heure: string | null;
  peage_entree: string | null;
  peage_sortie: string | null;
}

export interface AnalyseJustificatif {
  statut: 'en_cours' | 'terminee' | 'echec';
  champs?: ChampsExtraits;
  justificatif_a_risque?: { raison: string } | null;
  extraction_brute?: string | null;
}

export interface EvenementLisible {
  quand: string;
  qui: string;
  quoi: string;
  precision?: string;
  ton: 'neutre' | 'positif' | 'alerte';
}

export interface ChronologieNoteDonnees {
  jours: Array<{ jour: string; evenements: EvenementLisible[] }>;
  total: number;
}

export interface EvenementAudit {
  id: string;
  quand: string;
  qui: string;
  type: string;
  entite_type: string;
  entite_id: string;
  details: Record<string, unknown> | null;
}

export interface TableauBord {
  vue: 'collaborateur' | 'manager' | 'rh';
  vues_disponibles: string[];
  indicateurs: Array<{
    cle: string;
    libelle: string;
    valeur: number;
    unite: 'euros' | 'nombre';
    teinte: string;
    aide?: string;
  }>;
  kanban: Array<{ statut: string; notes: NoteTableauBord[] }>;
  depenses_non_associees: Array<{
    id: string;
    date_depense: string;
    categorie: string;
    description: string | null;
    montant_ttc: number;
    facturable: boolean;
    client_nom: string | null;
    salarie: string;
  }>;
  notes: NoteTableauBord[];
  graphiques: {
    par_categorie: LigneGraphique[];
    par_salarie: LigneGraphique[];
    par_mois: LigneGraphique[];
    par_client: LigneGraphique[];
    refacturable: LigneGraphique[];
    abonnements: LigneGraphique[];
  };
  cumuls_ik: Array<{ salarie: string; km: number; alerte: boolean }>;
  seuil_ik: number;
  options: {
    salaries: Array<{ id: string; nom: string }>;
    categories: Array<{ id: string; libelle: string }>;
    clients: string[];
  };
}

export interface LigneGraphique {
  libelle: string;
  montant: number;
  nombre?: number;
}

export interface NoteTableauBord {
  id: string;
  numero: string;
  titre: string | null;
  statut: string;
  date_note: string | null;
  date_remboursement: string | null;
  salarie: string;
  jours_attente: number | null;
  total_ttc: number;
  total_facturable: number;
  total_a_rembourser: number;
  nombre_depenses: number;
}

export interface Pilotage {
  annee: number;
  par_mois: Array<{ libelle: string; montant: number; nombre: number }>;
  par_salarie: Array<{ libelle: string; montant: number; nombre: number }>;
  par_categorie: Array<{ libelle: string; montant: number; nombre: number }>;
  refacturable: Array<{ libelle: string; montant: number; nombre: number }>;
  cumuls_ik: Array<{ salarie: string; km: number; alerte: boolean }>;
  seuil_ik: number;
  en_attente: Array<{ numero: string; salarie: string; chez: string; jours: number; montant: number }>;
  total_annee: number;
}

export interface ApercuExport {
  numero: string;
  notes: Array<{ id: string; numero: string; statut: string; date_note: string | null }>;
  nombre_depenses: number;
  nombre_lignes: number;
  total_debit: number;
  total_credit: number;
  desequilibres: Array<{ ordre: number; ecart: number }>;
}

export interface NoteDeFrais {
  id: string;
  salarie_id: string;
  numero: string;
  titre: string | null;
  statut: string;
  date_note: string | null;
  rejet_motif: string | null;
  /** Totaux calculés par le serveur — jamais recalculés côté écran. */
  total_ttc: number;
  total_facturable: number;
  total_a_rembourser: number;
  nombre_depenses: number;
  /** Facture fournisseur Books, une fois la note approuvée. */
  zoho_bill_id?: string | null;
  /** Date du versement, telle que Books la connaît. */
  date_remboursement?: string | null;
}

/** Une note en attente d'une décision de MA part. */
export interface NoteAValider extends NoteDeFrais {
  salarie_nom: string;
  controlees: number;
  etape: 'manager' | 'rh';
}

export interface Delegation {
  id: string;
  utilisateur_id: string;
  delegue_id: string;
  utilisateur_email: string;
  delegue_email: string;
  debut: string;
  fin: string;
  motif: string | null;
}

export interface GroupeDepenses {
  cle: string;
  libelle: string;
  client_nom: string | null;
  mission_nom: string | null;
  depense_ids: string[];
}

export interface AdresseTrajet {
  libelle: string;
  latitude: number;
  longitude: number;
  code_insee?: string | null;
}

/** Ce que le serveur renvoie avant enregistrement : distance ET calcul du barème. */
export interface ApercuTrajet {
  distance_km: number;
  duree_min: number;
  source: string;
  critere: string;
  montant: number;
  tranche_cv: string | null;
  coefficient: number;
  part_fixe: number;
  cumul_avant_km: number;
  cumul_apres_km: number;
  electrique: boolean;
  formule: string;
  alerte_cumul: boolean;
}

export interface VehiculeCourt {
  id: string;
  type_vehicule: string;
  cv_fiscaux: number | null;
  motorisation: string;
  immatriculation: string | null;
}

export interface AdresseFavorite extends AdresseTrajet {
  id: string;
  libelle_court: string;
}

export interface TrajetRecent {
  id: string;
  etapes: AdresseTrajet[];
  aller_retour: boolean;
  distance_km: number;
  nature_deplacement: string | null;
}

export interface LigneRegularisation {
  salarie_id: string;
  salarie: string;
  annee: number;
  total_km?: number;
  verse?: number;
  theorique?: number;
  ecart?: number;
  deja_regularise?: number;
  fiable?: boolean;
  erreur?: string;
}

export interface RecapitulatifIkDonnees {
  salarie: string;
  annee: number;
  vehicule: string;
  cumul_initial_km: number;
  lignes: Array<{
    date: string;
    trajet: string;
    aller_retour: boolean;
    distance_km: number;
    montant: number;
  }>;
  total_km: number;
  total_montant: number;
  cumul_final_km: number;
  depasse_seuil: boolean;
}

export interface ContexteTrajet {
  annee: number;
  cumul_km: number;
  seuil_alerte_km: number;
  vehicules: VehiculeCourt[];
  favoris: AdresseFavorite[];
  recents: TrajetRecent[];
}

export interface JustificatifEntrant {
  filename: string;
  contenu_base64: string;
  mime: string;
}
